import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getAvailableTutors, 
  getActiveTutorProfilesByUserId,
  getStudentTutorConnections, 
  getPublicSetting, 
  searchFriendById, 
  updateStudentTutorStatus,
  getPracticeLanguages,
  createTutorStudentChatSession
} from '../api/supabaseAPI';
import useUser from '../hooks/useUser';
import Loader from '../components/Loader';
import Message from '../components/Message';
import ToastStack from '../components/ToastStack';
import ModernCombobox from '../components/ModernCombobox';
import '../styles/TutorsList.css';
import '../styles/AdminDashboard.css';
import '../styles/Introduction.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function TutorsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: userData } = useUser();
  const userId = userData?.id;
  const practiceLanguage = userData?.settings?.language || 'en_us';

  // Tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'available';
  const availableLanguageFromQuery = searchParams.get('lang');
  const availableSortFromQuery = searchParams.get('sort');
  const myTutorsLanguageFromQuery = searchParams.get('my_lang');
  const searchLanguageFromQuery = searchParams.get('search_lang');
  const [requestTab, setRequestTab] = useState('received'); // 'received' or 'pending'
  const [selectedAvailableLanguage, setSelectedAvailableLanguage] = useState(availableLanguageFromQuery || practiceLanguage);
  const [availableSort, setAvailableSort] = useState(availableSortFromQuery || 'newest');
  const [selectedMyTutorsLanguage, setSelectedMyTutorsLanguage] = useState(myTutorsLanguageFromQuery || practiceLanguage);
  const [selectedSearchLanguage, setSelectedSearchLanguage] = useState(searchLanguageFromQuery || practiceLanguage);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [creatingTutorChatId, setCreatingTutorChatId] = useState('');

  // Toast State
  const [toast, setToast] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!toast.text) return undefined;
    const timer = setTimeout(() => {
      setToast({ text: '', type: '' });
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // R2 Base URL
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const { data: practiceLanguages = [], isLoading: isLoadingPracticeLanguages } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const practiceLanguageMap = useMemo(() => (
    new Map(practiceLanguages.map((item) => [item.value, item.label]))
  ), [practiceLanguages]);

  useEffect(() => {
    setSelectedAvailableLanguage(availableLanguageFromQuery || practiceLanguage);
  }, [availableLanguageFromQuery, practiceLanguage]);

  useEffect(() => {
    setSelectedMyTutorsLanguage(myTutorsLanguageFromQuery || practiceLanguage);
  }, [myTutorsLanguageFromQuery, practiceLanguage]);

  useEffect(() => {
    setAvailableSort(availableSortFromQuery || 'newest');
  }, [availableSortFromQuery]);

  useEffect(() => {
    setSelectedSearchLanguage(searchLanguageFromQuery || practiceLanguage);
  }, [searchLanguageFromQuery, practiceLanguage]);

  const availableLanguageOptions = useMemo(() => {
    const historyLanguages = (userData?.status || [])
      .map((item) => item.language)
      .filter(Boolean);

    const uniqueLanguages = [...new Set([practiceLanguage, ...historyLanguages].filter(Boolean))];

    return uniqueLanguages.map((code) => {
      return {
        value: code,
        label: practiceLanguageMap.get(code) || code.replace('_', '-').toUpperCase(),
      };
    });
  }, [practiceLanguage, practiceLanguageMap, userData?.status]);

  const myTutorsLanguageOptions = useMemo(() => ([
    { value: 'all', label: t('tutorsList.allLanguages') },
    ...availableLanguageOptions,
  ]), [availableLanguageOptions, t]);

  const searchLanguageOptions = useMemo(() => ([
    { value: 'all', label: t('tutorsList.allLanguages') },
    ...availableLanguageOptions,
  ]), [availableLanguageOptions, t]);

  const selectedLanguageLabel = useMemo(() => {
    return availableLanguageOptions.find((item) => item.value === selectedAvailableLanguage)?.label
      || selectedAvailableLanguage.replace('_', '-').toUpperCase();
  }, [availableLanguageOptions, selectedAvailableLanguage]);

  const availableSortOptions = useMemo(() => ([
    { value: 'newest', label: t('tutorsList.sortNewest') },
    { value: 'experience', label: t('tutorsList.sortMostExperienced') },
  ]), [t]);

  // Queries
  const { data: availableTutors, isLoading: isLoadingAvailable } = useQuery({
    queryKey: ['availableTutors', selectedAvailableLanguage],
    queryFn: () => getAvailableTutors(selectedAvailableLanguage),
    enabled: activeTab === 'available' && !!selectedAvailableLanguage,
  });

  const { data: myConnections, isLoading: isLoadingConnections } = useQuery({
    queryKey: ['studentTutorConnections', userId],
    queryFn: () => getStudentTutorConnections(userId),
    enabled: !!userId,
  });

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateStudentTutorStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentTutorConnections'] });
      setToast({ text: t('tutorsList.toast.statusUpdated'), type: 'success' });
    },
    onError: (err) => {
      setToast({ text: err.message || t('tutorsList.toast.statusUpdateFailed'), type: 'error' });
    }
  });

  // Helpers
  const getAvatarUrl = (path) => {
    if (!path) return '/Resources/imagine/default_avatar/default_avatar.jpeg';
    if (/^https?:\/\//i.test(path)) return path;
    return `${publicBaseUrl}/${path}`;
  };

  const getConnection = (tutorId) => {
    return myConnections?.find(c => c.tutor_id === tutorId && c.language === selectedAvailableLanguage);
  };

  const formatLanguageLabel = (code) => {
    return practiceLanguageMap.get(code) || code?.replace('_', '-').toUpperCase() || t('tutorsList.unknown');
  };

  const formatJoinedDate = (dateString) => {
    if (!dateString) return t('tutorsList.recentlyJoined');
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const formatExperience = (years) => {
    if (!Number.isFinite(years) || years <= 0) return t('tutorsList.experience.newTutor');
    if (years >= 50) return t('tutorsList.experience.experiencedTutor');
    if (years === 1) return t('tutorsList.experience.oneYear');
    return t('tutorsList.experience.multiYear', { years });
  };

  const sortedAvailableTutors = useMemo(() => {
    const tutors = [...(availableTutors || [])];

    if (availableSort === 'experience') {
      return tutors.sort((a, b) => {
        const yearsA = Number.isFinite(a.years_of_experience) ? a.years_of_experience : 0;
        const yearsB = Number.isFinite(b.years_of_experience) ? b.years_of_experience : 0;
        if (yearsB !== yearsA) return yearsB - yearsA;
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      });
    }

    return tutors.sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }, [availableTutors, availableSort]);

  const getTutorStatusLabel = (status) => {
    if (status === 'accepted') return t('tutorsList.status.myTutor');
    if (status === 'pending') return t('tutorsList.status.pending');
    if (status === 'rejected') return t('tutorsList.status.closed');
    return status;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const user = await searchFriendById(searchQuery.trim());
      if (user) {
        const tutorProfiles = await getActiveTutorProfilesByUserId(user.id, selectedSearchLanguage);
        setSearchResult({ user, tutorProfiles });
      } else {
        setSearchError(t('tutorsList.search.userNotFound'));
      }
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const handleTutorChatClick = async (tutor) => {
    const relationshipId = String(tutor?.relationship_id || '').trim();
    if (!relationshipId) {
      setToast({ text: t('tutorsList.toast.relationshipMissing'), type: 'error' });
      return;
    }

    setCreatingTutorChatId(relationshipId);
    try {
      const result = await createTutorStudentChatSession(relationshipId, tutor?.tutor_id || '');
      window.dispatchEvent(new CustomEvent('theralingua:open-chat-panel', {
        detail: {
          sectionKey: 'my_tutors',
          sessionId: result?.session_id || '',
          tutorStudentId: relationshipId,
        },
      }));
      setToast({
        text: result?.created_new ? t('tutorsList.toast.chatCreated') : t('tutorsList.toast.chatReady'),
        type: 'success',
      });
    } catch (error) {
      setToast({
        text: error instanceof Error ? error.message : t('tutorsList.toast.chatCreateFailed'),
        type: 'error',
      });
    } finally {
      setCreatingTutorChatId('');
    }
  };

  const buildTutorProfileState = () => {
    const params = new URLSearchParams({ tab: activeTab });

    if (activeTab === 'available') {
      params.set('lang', selectedAvailableLanguage);
      params.set('sort', availableSort);
    }

      if (activeTab === 'my_tutors') {
      params.set('my_lang', selectedMyTutorsLanguage);
    }

    if (activeTab === 'search') {
      params.set('search_lang', selectedSearchLanguage);
    }

    return {
      from: {
        pathname: '/tutors',
        search: `?${params.toString()}`,
      },
    };
  };

  // Filter My Tutors
  const activeTutors = myConnections?.filter(c => c.status === 'accepted') || [];
  const filteredMyTutors = selectedMyTutorsLanguage === 'all'
    ? activeTutors
    : activeTutors.filter((tutor) => tutor.language === selectedMyTutorsLanguage);
  const pendingRequests = myConnections?.filter(c => c.status === 'pending' && c.action_by === 'student') || [];
  const incomingInvites = myConnections?.filter(c => c.status === 'pending' && c.action_by === 'tutor') || [];

  return (
    <main className="tutors-list-container">
      <ToastStack items={[toast]} />

      <h1 className="page-title">{t('tutorsList.title', 'Find a Tutor')}</h1>
      <header className="tutors-list-header">
        <p className="tutors-list-subtitle">
          {t('tutorsList.subtitle', 'Connect with experienced tutors to practice your language skills.')}
        </p>
      </header>

      <div className="tutors-tabs-wrap">
        <div className="tutors-tabs">
          <button 
            className={`tutors-tab-btn ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => handleTabChange('available')}
          >
            {t('tutorsList.tabs.available')}
          </button>
          <button 
            className={`tutors-tab-btn ${activeTab === 'my_tutors' ? 'active' : ''}`}
            onClick={() => handleTabChange('my_tutors')}
          >
            {t('tutorsList.tabs.myTutors')}
          </button>
          <button 
            className={`tutors-tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => handleTabChange('requests')}
          >
            {t('tutorsList.tabs.requests')}
            {(incomingInvites.length > 0) && (
                <span className="tutors-tab-count">
                    {incomingInvites.length}
                </span>
            )}
          </button>
          <button 
            className={`tutors-tab-btn ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => handleTabChange('search')}
          >
            {t('tutorsList.tabs.search')}
          </button>
        </div>
      </div>

      {activeTab === 'available' && (
        <>
          {isLoadingAvailable || isLoadingPracticeLanguages ? <Loader /> : (
            <>
              <section className="available-tutors-panel">
                <div className="available-tutors-panel__intro">
                  <p className="available-tutors-panel__eyebrow">{t('tutorsList.available.eyebrow')}</p>
                  <div className="available-tutors-panel__summary">
                    <h2>{selectedLanguageLabel}</h2>
                    <p>
                      {t('tutorsList.available.summary')}
                    </p>
                  </div>
                </div>
                <div className="available-tutors-panel__controls">
                  <div className="available-tutors-filter-grid">
                    <div className="available-tutors-filter">
                    <span className="available-tutors-filter__label">{t('tutorsList.practiceLanguage')}</span>
                    <div className="available-tutors-combobox">
                      <ModernCombobox
                        options={availableLanguageOptions}
                        value={selectedAvailableLanguage}
                        onChange={setSelectedAvailableLanguage}
                        placeholder={t('tutorsList.selectPracticeLanguage')}
                        allowTyping={false}
                      />
                    </div>
                  </div>
                    <div className="available-tutors-filter">
                      <span className="available-tutors-filter__label">{t('tutorsList.sortBy')}</span>
                      <div className="available-tutors-combobox">
                        <ModernCombobox
                          options={availableSortOptions}
                          value={availableSort}
                          onChange={setAvailableSort}
                          placeholder={t('tutorsList.selectSorting')}
                          allowTyping={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="available-tutors-panel__stats">
                    <div className="available-tutors-stat">
                      <span className="available-tutors-stat__value">{availableTutors?.length || 0}</span>
                      <span className="available-tutors-stat__label">{t('tutorsList.activeTutors')}</span>
                    </div>
                    <div className="available-tutors-stat">
                      <span className="available-tutors-stat__value">{formatLanguageLabel(selectedAvailableLanguage)}</span>
                      <span className="available-tutors-stat__label">{t('tutorsList.currentFilter')}</span>
                    </div>
                  </div>
                </div>
              </section>

              <div className="tutors-grid">
              {sortedAvailableTutors.map((tutor) => {
                const conn = getConnection(tutor.uid);
                
                return (
                  <div
                    key={tutor.uid}
                    className="tutor-public-card"
                    onClick={() => navigate(`/tutor/${tutor.uid}/${tutor.language}`, { state: buildTutorProfileState() })}
                  >
                    <div className="tutor-card-accent" aria-hidden="true"></div>
                    <div className="tutor-card-header">
                      <div className="tutor-info-wrapper">
                        <div className="tutor-avatar-wrapper">
                          <img src={getAvatarUrl(tutor.avatar)} className="tutor-avatar-img" alt={tutor.full_name} />
                        </div>
                        <div className="tutor-details">
                          <div className="tutor-name-row">
                            <h3>{tutor.display_name || tutor.full_name}</h3>
                            {tutor.display_name && tutor.display_name !== tutor.full_name && (
                              <span className="tutor-full-name">{tutor.full_name}</span>
                            )}
                          </div>
                          <div className="tutor-headline">
                            {tutor.headline || formatExperience(tutor.years_of_experience)}
                          </div>
                        </div>
                      </div>
                      {conn && (
                        <span className={`tutor-lang-badge status-${conn.status}`}>
                          {getTutorStatusLabel(conn.status)}
                        </span>
                      )}
                    </div>
                    <div className="tutor-card-body">
                      <div className="tutor-meta-row">
                        <span className="tutor-meta-chip">{formatLanguageLabel(tutor.language)}</span>
                        <span className="tutor-meta-chip">{formatExperience(tutor.years_of_experience)}</span>
                        <span className="tutor-meta-chip">{t('tutorsList.joined', { date: formatJoinedDate(tutor.created_at) })}</span>
                      </div>
                      <div className="tutor-bio">
                        {tutor.bio || tutor.self_introduction || t('tutorsList.noIntroduction')}
                      </div>
                      <div className="tutor-card-footer">
                        <div className="tutor-card-footer__hint">{t('tutorsList.available.footerHint')}</div>
                        <button className="view-profile-btn">
                          {t('tutorsList.viewProfile')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!availableTutors || availableTutors.length === 0) && (
                <div className="tutors-empty">
                  <div className="tutors-empty__title">{t('tutorsList.available.emptyTitle')}</div>
                  <div className="tutors-empty__body">
                    {t('tutorsList.available.emptyBody', { language: selectedLanguageLabel })}
                  </div>
                </div>
              )}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'my_tutors' && (
        <>
          {isLoadingConnections || isLoadingPracticeLanguages ? <Loader /> : (
            <>
              <section className="my-tutors-panel">
                <div className="my-tutors-panel__intro">
                  <p className="my-tutors-panel__eyebrow">{t('tutorsList.myTutors.eyebrow')}</p>
                  <div className="my-tutors-panel__summary">
                    <h2>{selectedMyTutorsLanguage === 'all' ? t('tutorsList.allLanguages') : formatLanguageLabel(selectedMyTutorsLanguage)}</h2>
                    <p>
                      {t('tutorsList.myTutors.summary')}
                    </p>
                  </div>
                </div>
                <div className="my-tutors-panel__controls">
                  <div className="available-tutors-filter">
                    <span className="available-tutors-filter__label">{t('tutorsList.practiceLanguage')}</span>
                    <div className="available-tutors-combobox">
                      <ModernCombobox
                        options={myTutorsLanguageOptions}
                        value={selectedMyTutorsLanguage}
                        onChange={setSelectedMyTutorsLanguage}
                        placeholder={t('tutorsList.selectPracticeLanguage')}
                        allowTyping={false}
                      />
                    </div>
                  </div>
                  <div className="my-tutors-panel__stats">
                    <div className="available-tutors-stat">
                      <span className="available-tutors-stat__value">{filteredMyTutors.length}</span>
                      <span className="available-tutors-stat__label">{t('tutorsList.activeTutors')}</span>
                    </div>
                    <div className="available-tutors-stat">
                      <span className="available-tutors-stat__value">{activeTutors.length}</span>
                      <span className="available-tutors-stat__label">{t('tutorsList.allLanguages')}</span>
                    </div>
                  </div>
                </div>
              </section>

              <div className="tutors-grid">
                {filteredMyTutors.map(tutor => (
                  <div key={tutor.relationship_id} className="tutor-public-card tutor-public-card--connected">
                    <div className="tutor-card-accent" aria-hidden="true"></div>
                    <div className="tutor-card-header">
                      <div className="tutor-info-wrapper">
                        <div className="tutor-avatar-wrapper">
                          <img src={getAvatarUrl(tutor.avatar)} className="tutor-avatar-img" alt={tutor.full_name || t('tutorsList.tutorAvatar')} />
                        </div>
                        <div className="tutor-details">
                          <div className="tutor-name-row">
                            <h3>{tutor.full_name || t('tutorsList.unknown')}</h3>
                          </div>
                          <div className="tutor-headline">
                            {tutor.headline || formatLanguageLabel(tutor.language)}
                          </div>
                        </div>
                      </div>
                      <span className="tutor-lang-badge status-accepted">{t('tutorsList.status.myTutor')}</span>
                    </div>
                    <div className="tutor-card-body">
                      <div className="tutor-meta-row">
                        <span className="tutor-meta-chip">{formatLanguageLabel(tutor.language)}</span>
                        <span className="tutor-meta-chip">{t('tutorsList.myTutors.connectedTutor')}</span>
                      </div>
                      <div className="tutor-bio">
                        {tutor.bio || t('tutorsList.noIntroduction')}
                      </div>
                      <div className="tutor-card-footer">
                        <div className="tutor-card-footer__hint">{t('tutorsList.myTutors.footerHint')}</div>
                        <div className="tutor-card-footer__actions">
                          <button 
                            type="button"
                            className="view-profile-btn"
                            onClick={() => navigate(`/tutor/${tutor.tutor_id}/${tutor.language}`, { state: buildTutorProfileState() })}
                          >
                            {t('tutorsList.viewProfile')}
                          </button>
                          <button
                            type="button"
                            className="tutor-chat-icon-btn"
                            aria-label={t('tutorsList.chatAria', { name: tutor.full_name || t('tutorsList.thisTutor') })}
                            title={t('tutorsList.tutorChat')}
                            onClick={() => handleTutorChatClick(tutor)}
                            disabled={creatingTutorChatId === tutor.relationship_id}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredMyTutors.length === 0 && (
                  <div className="tutors-empty">
                    <div className="tutors-empty__title">{t('tutorsList.myTutors.emptyTitle')}</div>
                    <div className="tutors-empty__body">
                      {t('tutorsList.myTutors.emptyBody', { language: selectedMyTutorsLanguage === 'all' ? t('tutorsList.allLanguagesLower') : formatLanguageLabel(selectedMyTutorsLanguage) })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'requests' && (
        <div className="admin-list-grid">
           {/* Sub Tabs Container - Full width of the grid context */}
           <div className="requests-subtabs">
              <button 
                className={`btn-small ${requestTab === 'received' ? 'active-sub-tab' : 'btn-outline'}`}
                data-active={requestTab === 'received'}
                onClick={() => setRequestTab('received')}
              >
                <span>{t('tutorsList.requests.received')}</span>
                {incomingInvites.length > 0 && (
                  <span className="tab-count-badge">{incomingInvites.length}</span>
                )}
              </button>
              <button 
                className={`btn-small ${requestTab === 'pending' ? 'active-sub-tab' : 'btn-outline'}`}
                data-active={requestTab === 'pending'}
                onClick={() => setRequestTab('pending')}
              >
                <span>{t('tutorsList.requests.pending')}</span>
                {pendingRequests.length > 0 && (
                  <span className="tab-count-badge">{pendingRequests.length}</span>
                )}
              </button>
           </div>

           {isLoadingConnections || isLoadingPracticeLanguages ? <Loader /> : (
             <>
               {requestTab === 'received' && (
                 <>
                   {incomingInvites.map(invite => (
                     <div key={invite.relationship_id} className="application-card">
                       <div className="app-card-header">
                         <div className="app-applicant">
                           <img src={getAvatarUrl(invite.avatar)} className="app-avatar" alt={t('tutorsList.avatar')} />
                           <div className="app-info">
                             <h3>{invite.full_name || t('tutorsList.unknown')}</h3>
                             <p>{t('tutorsList.requests.invitedToLearn', { language: invite.language.replace('_', '-') })}</p>
                           </div>
                         </div>
                         <span className="lang-badge">{invite.language.replace('_', '-')}</span>
                       </div>
                       <div className="app-actions">
                         <button 
                           className="reject-btn"
                           onClick={() => updateStatusMutation.mutate({id: invite.relationship_id, status: 'rejected'})}
                         >
                           {t('tutorsList.requests.decline')}
                         </button>
                         <button 
                           className="approve-btn"
                           onClick={() => updateStatusMutation.mutate({id: invite.relationship_id, status: 'accepted'})}
                         >
                           {t('tutorsList.requests.accept')}
                         </button>
                     </div>
                   </div>
                  ))}
                   {incomingInvites.length === 0 && <div className="tutors-empty tutors-empty--span-full">{t('tutorsList.requests.noIncoming')}</div>}
                 </>
               )}

               {requestTab === 'pending' && (
                 <>
                   {pendingRequests.map(req => (
                     <div key={req.relationship_id} className="application-card tutor-request-card">
                       <div className="app-card-header">
                         <div className="app-applicant">
                           <img src={getAvatarUrl(req.avatar)} className="app-avatar" alt={t('tutorsList.avatar')} />
                           <div className="app-info">
                             <h3>{req.full_name || t('tutorsList.unknown')}</h3>
                             <p>{t('tutorsList.requests.waitingApproval')}</p>
                           </div>
                         </div>
                         <span className="lang-badge">{req.language.replace('_', '-')}</span>
                       </div>
                       <div className="app-actions">
                         <button 
                           className="cancel-invite-btn"
                           onClick={() => updateStatusMutation.mutate({id: req.relationship_id, status: 'rejected'})} 
                         >
                           {t('tutorsList.requests.cancelRequest')}
                         </button>
                     </div>
                   </div>
                  ))}
                   {pendingRequests.length === 0 && <div className="tutors-empty tutors-empty--span-full">{t('tutorsList.requests.noPending')}</div>}
                 </>
               )}
             </>
           )}
        </div>
      )}

      {activeTab === 'search' && (
        <div className="tutors-search">
          <div className="search-filter-row">
            <div className="available-tutors-filter">
              <span className="available-tutors-filter__label">{t('tutorsList.search.tutorLanguage')}</span>
              <div className="available-tutors-combobox">
                <ModernCombobox
                  options={searchLanguageOptions}
                  value={selectedSearchLanguage}
                  onChange={setSelectedSearchLanguage}
                  placeholder={t('tutorsList.search.selectTutorLanguage')}
                  allowTyping={false}
                />
              </div>
            </div>
          </div>

          <form className="search-form" onSubmit={handleSearch}>
            <div className="search-input-wrap">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input 
                className="search-input"
                placeholder={t('tutorsList.search.inputPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="search-btn" disabled={isSearching}>
              {isSearching ? t('tutorsList.search.searching') : t('tutorsList.search.search')}
            </button>
          </form>

          {searchError && <Message text={searchError} type="error" />}

          {searchResult && (
            <>
              {searchResult.tutorProfiles.length > 0 ? (
                <>
                  <div className="search-result-summary search-result-summary--compact">
                    <div className="search-result-summary__meta">
                      <span className="tutor-meta-chip">
                        {selectedSearchLanguage === 'all' ? t('tutorsList.allLanguages') : formatLanguageLabel(selectedSearchLanguage)}
                      </span>
                      <span className="tutor-meta-chip">{t('tutorsList.search.profileCount', { count: searchResult.tutorProfiles.length })}</span>
                    </div>
                  </div>

                  <div className="tutors-grid">
                  {searchResult.tutorProfiles.map((tutor) => (
                    <div
                      key={`${tutor.uid}-${tutor.language}`}
                      className="tutor-public-card"
                      onClick={() => navigate(`/tutor/${tutor.uid}/${tutor.language}`, { state: buildTutorProfileState() })}
                    >
                      <div className="tutor-card-accent" aria-hidden="true"></div>
                      <div className="tutor-card-header">
                        <div className="tutor-info-wrapper">
                          <div className="tutor-avatar-wrapper">
                            <img src={getAvatarUrl(tutor.avatar)} className="tutor-avatar-img" alt={tutor.full_name || tutor.display_name || t('tutorsList.tutorAvatar')} />
                          </div>
                          <div className="tutor-details">
                            <div className="tutor-name-row">
                              <h3>{tutor.display_name || tutor.full_name || t('tutorsList.search.unknownTutor')}</h3>
                              {tutor.display_name && tutor.full_name && tutor.display_name !== tutor.full_name && (
                                <span className="tutor-full-name">{tutor.full_name}</span>
                              )}
                            </div>
                            <div className="tutor-headline">
                              {tutor.headline || formatExperience(tutor.years_of_experience)}
                            </div>
                          </div>
                        </div>
                        <span className="tutor-lang-badge">{formatLanguageLabel(tutor.language)}</span>
                      </div>
                      <div className="tutor-card-body">
                        <div className="tutor-meta-row">
                          <span className="tutor-meta-chip">{formatLanguageLabel(tutor.language)}</span>
                          <span className="tutor-meta-chip">{formatExperience(tutor.years_of_experience)}</span>
                          <span className="tutor-meta-chip">{t('tutorsList.joined', { date: formatJoinedDate(tutor.created_at) })}</span>
                        </div>
                        <div className="tutor-bio">
                          {tutor.bio || t('tutorsList.noIntroduction')}
                        </div>
                        <div className="tutor-card-footer">
                          <div className="tutor-card-footer__hint">{t('tutorsList.search.footerHint')}</div>
                          <button className="view-profile-btn">{t('tutorsList.viewProfile')}</button>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </>
              ) : (
                <div className="tutors-empty">
                  <div className="tutors-empty__title">{t('tutorsList.search.emptyTitle')}</div>
                  <div className="tutors-empty__body">
                    {t('tutorsList.search.emptyBody', { language: selectedSearchLanguage === 'all' ? t('tutorsList.anyLanguage') : formatLanguageLabel(selectedSearchLanguage) })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </main>
  );
}
